import { Box, HBox, VBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import { Typo } from "@ui/text/Typo"
import QRCode from "qrcode"
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { GD } from "src/GD"
import { CARD_HOLDER_REJECTED_ERROR_CODE, getFriendlyErrorMessage, isKycAlreadyApproved, isKycPhoneCorrectionError } from "src/utils/errors"
import { isKycInProgress } from "src/utils/kyc"
import { openCardIssuanceError } from "./CardIssuanceError"
import { KycIssuersMatrix } from "./KycIssuersMatrix"
import { KycCallout, KycStepper, KycVerifyCard, type KycTone } from "./ProviderKycStatus"
import { DS } from "../ds/Kit"
import { Spinner } from "@ui/spinner/Spinner"
import { S_UNIVERSAL_DIALOG } from "./universalDialog/signals"
import { getIssuerKycStatus } from "src/utils/issuerKyc"
import { useOptionalIssuer } from "../issuer/IssuerProvider"
import {
	isRunningInNativeApp,
	openExternalUrl
} from "src/utils/nativePlatform"
import { getCardHolderRecovery } from "src/utils/cardHolderRecovery"
import { openIssuerReviewWait } from "./IssuerReviewWait"
import { hasIssuerReviewPending, isIssuerReviewError, isIssuerReviewStatus, reviewFromError } from "src/utils/issuerReview"

const KycPhoneForm = lazy(() => import("./KycPhoneForm").then(module => ({ default: module.KycPhoneForm })))

const POLL_INTERVAL_MS = 3000
const KYC_REQUIRED_ERROR_CODE = 2613
const CARD_HOLDER_INCOMPLETE_ERROR_CODE = 2201

const KycRecovery = ({
	retry,
	onSuccess,
	initialError,
	purpose = "card",
	forceOurKycRestart: initialForceOurKycRestart = false
}: {
	retry: () => Promise<IAPIResponse<unknown>>
	onSuccess?: () => void
	initialError?: IError
	purpose?: "card" | "funding"
	forceOurKycRestart?: boolean
}) => {
	const selectedWorkspaceIssuerId = useOptionalIssuer()?.selectedIssuerId ?? null
	const targetIssuer = initialError?.issuer ?? selectedWorkspaceIssuerId
	const nativeApp = isRunningInNativeApp()
	const [mode, setMode] = useState<"our" | "issuer">("our")
	const [status, setStatus] = useState<TKycStatus | null>(null)
	const [reason, setReason] = useState<string | null>(null)
	const [issuers, setIssuers] = useState<IKycIssuer[] | undefined>()
	const [loading, setLoading] = useState(true)
	const [starting, setStarting] = useState(false)
	const [retrying, setRetrying] = useState(false)
	const [forceOurKycRestart, setForceOurKycRestart] = useState(initialForceOurKycRestart)
	const [verificationUrl, setVerificationUrl] = useState<string | null>(null)
	const [qrDataUrl, setQrDataUrl] = useState("")
	const [phoneContinuation, setPhoneContinuation] = useState<"start" | "retry" | null>(null)
	const [serverMessage, setServerMessage] = useState(
		purpose === "funding"
			? "Complete identity verification before adding funds."
			: initialError ? getFriendlyErrorMessage(initialError, "Identity verification is not ready") : null
	)
	const statusTimer = useRef<ReturnType<typeof setInterval> | null>(null)
	const approvalHandled = useRef(false)
	const issuerApprovalHandled = useRef(false)
	const retryingRef = useRef(false)
	const mounted = useRef(false)
	const lastRecovery = useRef<ReturnType<typeof getCardHolderRecovery> | null>(
		initialError?.code === CARD_HOLDER_INCOMPLETE_ERROR_CODE
			? getCardHolderRecovery(initialError)
			: null
	)

	useEffect(() => {
		mounted.current = true
		return () => { mounted.current = false }
	}, [])

	const stopStatusPolling = useCallback(() => {
		if (statusTimer.current) {
			clearInterval(statusTimer.current)
			statusTimer.current = null
		}
	}, [])


	const retryCardCreation = useCallback(async (afterPhone = false) => {
		if (!mounted.current && !afterPhone) return
		if (retryingRef.current) return
		retryingRef.current = true
		setRetrying(true)
		const res = await retry()
		retryingRef.current = false
		if (!mounted.current && !afterPhone) return
		setRetrying(false)

		// Funding owns address/error rendering and must not enter card issuance
		// recovery or wait for provider approval after our KYC is approved.
		if (purpose === "funding") {
			if (!res.error) onSuccess?.()
			S_UNIVERSAL_DIALOG.invoke(null)
			return
		}

		if (!res.error) {
			onSuccess?.()
			S_UNIVERSAL_DIALOG.invoke(null)
			return
		}
		if (res.error.code === CARD_HOLDER_INCOMPLETE_ERROR_CODE) {
			const recovery = getCardHolderRecovery(res.error)
			if (recovery === "error" || recovery === lastRecovery.current) {
				stopStatusPolling()
				openCardIssuanceError(res.error)
				return
			}
			lastRecovery.current = recovery
			if (recovery === "phone") {
				stopStatusPolling()
				setPhoneContinuation("retry")
				return
			}
			if (recovery === "kyc") {
				if (afterPhone) {
					openSimplifiedKyc(retry, onSuccess, { ...res.error, issuer: res.error.issuer ?? targetIssuer ?? undefined }, { forceOurKycRestart: true })
					return
				}
				setForceOurKycRestart(true)
				setMode("our")
				setStatus("none")
				setReason(getFriendlyErrorMessage(res.error))
				setVerificationUrl(null)
				return
			}
			return
		}
		if (res.error.code === KYC_REQUIRED_ERROR_CODE) {
			const issuer = res.error.issuer ?? targetIssuer
			if ((isIssuerReviewError(res.error) || !afterPhone) && issuer) {
				stopStatusPolling()
				openIssuerReviewWait(issuer, reviewFromError(res.error))
				return
			}
			if (afterPhone) {
				openSimplifiedKyc(retry, onSuccess, { ...res.error, issuer: res.error.issuer ?? targetIssuer ?? undefined })
				return
			}
			setServerMessage(
				getFriendlyErrorMessage(res.error, "The issuer is still reviewing the card holder")
			)
			setMode("issuer")
			return
		}
		if (res.error.code === CARD_HOLDER_REJECTED_ERROR_CODE || afterPhone) {
			stopStatusPolling()
			openCardIssuanceError(res.error)
			return
		}
		toast.error(
			getFriendlyErrorMessage(res.error, "Card issuance still failed after verification")
		)
	}, [onSuccess, purpose, retry, stopStatusPolling, targetIssuer])

	const fetchStatus = useCallback(
		async (retryWhenApproved: boolean) => {
			const res = await GD.REQ_KYC_STATUS.request({ issuer: targetIssuer ?? undefined })
			if (!mounted.current) return null
			if (res.error) {
				setReason(getFriendlyErrorMessage(res.error, "Could not check KYC status"))
				return null
			}

			const nextStatus = res.data.our_kyc_status
			if (purpose !== "funding" && isIssuerReviewStatus(res.data) && targetIssuer) {
				stopStatusPolling()
				openIssuerReviewWait(targetIssuer, res.data.review, undefined, res.data.issuer_label)
				return null
			}
			setStatus(nextStatus)
			setReason(res.data.our_kyc_reason)
			if (!isKycInProgress(nextStatus)) {
				stopStatusPolling()
				setVerificationUrl(null)
				setQrDataUrl("")
			}

			if (
				nextStatus === "approved" &&
				retryWhenApproved &&
				!approvalHandled.current
			) {
				approvalHandled.current = true
				void retryCardCreation()
			}
			return nextStatus
		},
		[purpose, retryCardCreation, stopStatusPolling, targetIssuer]
	)

	const startStatusPolling = useCallback(() => {
		if (!mounted.current) return
		stopStatusPolling()
		statusTimer.current = setInterval(() => {
			void fetchStatus(true)
		}, POLL_INTERVAL_MS)
	}, [fetchStatus, stopStatusPolling])

	const openKycUrl = useCallback(async (url: string) => {
		try {
			const opened = await openExternalUrl(url)
			if (opened) return true
			toast.info("Could not open the verification form. Try again.")
			return false
		} catch (error) {
			console.error(error)
			toast.error("Could not open identity verification")
			return false
		}
	}, [])

	const returnToKycUrl = useCallback(async (url: string) => {
		return openKycUrl(url)
	}, [openKycUrl])

	const fetchIssuers = useCallback(async () => {
		const res = await GD.REQ_KYC_ISSUERS.request({ issuer: targetIssuer ?? undefined })
		if (!mounted.current) return
		if (res.error) {
			setServerMessage(getFriendlyErrorMessage(res.error, "Could not check issuer status"))
			return
		}
		setIssuers(res.data)
		const nextStatus = getIssuerKycStatus(res.data, targetIssuer)
		const selectedIssuer = res.data.find(item => item.issuer === targetIssuer)
		if (hasIssuerReviewPending(selectedIssuer, status ?? undefined) && targetIssuer) {
			stopStatusPolling()
			openIssuerReviewWait(targetIssuer, selectedIssuer?.review, undefined, selectedIssuer?.issuer_label || selectedIssuer?.name)
			return
		}
		if (nextStatus === "approved" && !issuerApprovalHandled.current) {
			issuerApprovalHandled.current = true
			void retryCardCreation()
		}
	}, [retryCardCreation, status, stopStatusPolling, targetIssuer])

	useEffect(() => {
		let active = true
		;(async () => {
			const nextStatus = await fetchStatus(false)
			if (!active) return
			setLoading(false)

			// A cards/create 2201 can route here for both first-time and repeat KYC.
			// Do not let that recovery hint override the authoritative KYC status:
			// new and active sessions must be presented as Start/Continue, not Restart.
			const hasActiveOrNoVerification =
				nextStatus === "none" || isKycInProgress(nextStatus)
			if (forceOurKycRestart && hasActiveOrNoVerification) {
				setServerMessage(null)
			}

			if (nextStatus === "approved" && !forceOurKycRestart) {
				if (purpose === "funding") void retryCardCreation()
				else setMode("issuer")
			} else if (isKycInProgress(nextStatus)) {
				startStatusPolling()
			}
		})()
		return () => {
			active = false
			stopStatusPolling()
		}
	}, [fetchStatus, forceOurKycRestart, purpose, retryCardCreation, startStatusPolling, stopStatusPolling])

	useEffect(() => {
		if (mode !== "issuer") {
			return
		}
		void fetchIssuers()
	}, [fetchIssuers, mode])

	useEffect(() => {
		let active = true
		setQrDataUrl("")
		if (!verificationUrl) {
			setQrDataUrl("")
			return
		}
		QRCode.toDataURL(verificationUrl, { width: 220, margin: 1 })
			.then(dataUrl => {
				if (active) setQrDataUrl(dataUrl)
			})
			.catch(() => {
				if (active) setQrDataUrl("")
			})
		return () => {
			active = false
		}
	}, [verificationUrl])

	const startKycAfterPhone = useCallback(async () => {
		setPhoneContinuation(null)
		setStarting(true)
		const res = await GD.REQ_KYC_START.request({ issuer: targetIssuer ?? undefined })
		setStarting(false)
		if (res.error) {
			if (isKycPhoneCorrectionError(res.error)) {
				setPhoneContinuation("start")
				return
			}
			if (isKycAlreadyApproved(res.error)) {
				approvalHandled.current = false
				await fetchStatus(true)
				return
			}
			toast.error(getFriendlyErrorMessage(res.error, "Failed to start KYC"))
			return
		}
		if (!res.data.verification_url) {
			toast.error("Verification URL is missing")
			return
		}
		const url = res.data.verification_url
		approvalHandled.current = false
		setVerificationUrl(url)
		setStatus("pending")
		setReason(null)
		if (isRunningInNativeApp()) await openKycUrl(url)
		startStatusPolling()
	}, [fetchStatus, openKycUrl, startStatusPolling, targetIssuer])

	const startKyc = useCallback(async () => {
		const resuming = isKycInProgress(status)
		if (verificationUrl && resuming) {
			await returnToKycUrl(verificationUrl)
			return
		}

		setStarting(true)
		const res = await GD.REQ_KYC_START.request({ issuer: targetIssuer ?? undefined })
		setStarting(false)
		if (res.error) {
			if (isKycPhoneCorrectionError(res.error)) {
				setPhoneContinuation("start")
				return
			}
			if (isKycAlreadyApproved(res.error)) {
				approvalHandled.current = false
				await fetchStatus(true)
				return
			}
			toast.error(getFriendlyErrorMessage(res.error, "Failed to start KYC"))
			return
		}

		const url = res.data.verification_url
		if (!url) {
			toast.error("Verification URL is missing")
			return
		}
		approvalHandled.current = false
		setVerificationUrl(url)
		setStatus("pending")
		setReason(null)
		if (resuming || isRunningInNativeApp()) await returnToKycUrl(url)
		startStatusPolling()
	}, [
		fetchStatus,
		returnToKycUrl,
		startStatusPolling,
		status,
		targetIssuer,
		verificationUrl
	])

	if (phoneContinuation) {
		return (
			<VBox gap='18px'>
				<KycStepper current={0} />
				<Suspense fallback={<Typo variant='caption'>Loading phone form…</Typo>}>
					<KycPhoneForm
						issuer={targetIssuer ?? undefined}
						onSuccess={phoneContinuation === "retry"
							? async () => {
								setPhoneContinuation(null)
								await retryCardCreation(true)
							}
							: startKycAfterPhone}
						onCancel={() => setPhoneContinuation(null)}
						embedded
					/>
				</Suspense>
			</VBox>
		)
	}

	if (retrying) {
		return (
			<VBox gap='18px'>
				<KycStepper current={3} />
				<VBox gap='6px' role='status' aria-label='Creating card after verification' sx={{ alignItems: "center", textAlign: "center", padding: "12px 0" }}>
					<Box aria-hidden sx={{ width: "72px", height: "72px", borderRadius: "50%", display: "grid", placeItems: "center", marginBottom: "10px", background: "radial-gradient(circle, #FFFFFF 38%, #E8F0F8 39%, #E8F0F8 62%, #F6F7FB 63%)", boxShadow: "0 0 0 1px #E0E3FB" }}>
						<Spinner size={32} color={DS.accent} />
					</Box>
					<Typo sx={{ fontSize: "18px", fontWeight: 700, color: DS.ink }}>
						Creating your card…
					</Typo>
					<Typo sx={{ fontSize: "14px", color: DS.muted, lineHeight: 1.5 }}>
						Identity verification is approved. Card creation is being retried automatically; this window will show the result.
					</Typo>
				</VBox>
			</VBox>
		)
	}

	if (loading) {
		return (
			<HBox gap='12px' sx={{ alignItems: "center", justifyContent: "center", padding: "28px 0", color: DS.muted }}>
				<Spinner size={22} color={DS.accent} />
				<Typo variant='medium' sx={{ color: DS.muted }}>
					Checking identity verification…
				</Typo>
			</HBox>
		)
	}

	if (mode === "issuer") {
		return (
			<VBox gap='18px'>
				<KycStepper current={2} />
				<KycCallout tone='pending' title='Card provider is checking your details'>
					{serverMessage ||
						"Your identity verification is approved. The issuer is still reviewing the card holder; status updates automatically."}
				</KycCallout>
				<KycIssuersMatrix data={issuers} />
				<HBox gap='8px' sx={{ flexWrap: "wrap" }}>
					<Button
						title={retrying ? "Trying…" : "Try card creation again"}
						variant='primary'
						disabled={retrying || getIssuerKycStatus(issuers ?? [], targetIssuer) !== "approved"}
						onClick={() => void retryCardCreation()}
					/>
					<Button
						title='Refresh issuer status'
						variant='secondary'
						onClick={() => void fetchIssuers()}
					/>
				</HBox>
			</VBox>
		)
	}

	const inProgress = isKycInProgress(status)
	// A freshly created account can already report `pending` before the user has
	// ever opened the provider form. Only claim that there is a form to return
	// to when this dialog actually owns a verification URL.
	const canReturnToVerification = inProgress && Boolean(verificationUrl)
	const restartRequestedByIssuance =
		forceOurKycRestart && status !== "none" && !inProgress
	const actionTitle = starting
		? canReturnToVerification
			? "Opening verification form…"
			: "Starting…"
		: restartRequestedByIssuance
			? "Restart identity verification"
			: canReturnToVerification
				? "Return to verification form"
					: status === "declined" || status === "expired" || status === "error"
						? "Restart identity verification"
						: "Start identity verification"
	const calloutTone: KycTone = canReturnToVerification || inProgress
		? "pending"
		: status === "declined" || status === "expired" || status === "error"
			? status
			: "info"
	const showQr = !nativeApp && inProgress && Boolean(qrDataUrl) && Boolean(verificationUrl)

	return (
		<VBox gap='18px'>
			<KycStepper current={1} />
			<KycCallout tone={calloutTone} title={canReturnToVerification ? "Verification in progress" : "Verify your identity"}>
				{canReturnToVerification
					? "You started identity verification but did not finish it. Return to the verification form and complete the remaining details. This window stays open, checks status automatically, and retries card creation after approval."
					: reason || serverMessage || (purpose === "funding" ? "Complete identity verification before adding funds." : "This BIN requires approved identity verification.")}
			</KycCallout>
			<KycVerifyCard
				qrDataUrl={showQr ? qrDataUrl : undefined}
				qrSize={200}
				hint='Or scan this code to continue on your phone'
			>
				<HBox gap='8px' sx={{ flexWrap: "wrap" }}>
					<Button
						title={actionTitle}
						variant='primary'
						disabled={starting}
						onClick={() => void startKyc()}
						sx={{ minHeight: "44px", borderRadius: "12px" }}
					/>
				</HBox>
			</KycVerifyCard>
		</VBox>
	)
}

let recoverySequence = 0

/**
 * Recovery for cards/create 2613. The gate is our_kyc_status; when our KYC is
 * already approved, issuer review is displayed and polled through kyc/issuers.
 * Funding skips issuer review and hands control back after one address retry.
 * Its retry callback owns rendering both the address and any remaining error.
 */
export const openSimplifiedKyc = (
	retry: () => Promise<IAPIResponse<unknown>>,
	onSuccess?: () => void,
	initialError?: IError,
	options?: { forceOurKycRestart?: boolean; purpose?: "card" | "funding" }
) => {
	if (options?.purpose !== "funding" && isIssuerReviewError(initialError) && initialError?.issuer) {
		openIssuerReviewWait(initialError.issuer, reviewFromError(initialError))
		return
	}
	S_UNIVERSAL_DIALOG.invoke({
		label: "Identity verification required",
		content: (
			<KycRecovery
				key={++recoverySequence}
				retry={retry}
				onSuccess={onSuccess}
				initialError={initialError}
				purpose={options?.purpose}
				forceOurKycRestart={options?.forceOurKycRestart}
			/>
		),
		closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null)
	})
}
